import { Defaults, Routing, UserConstants } from "@common/_constants";
import {
    formatClientRoute,
    LocationService,
    UserService,
} from "@common/_services";
import { Button } from "@common/components/Atoms/Button";
import ContactError from "@common/components/Atoms/Error";
import {
    FormHandler,
    Input,
    Rules,
    Select,
} from "@common/components/Atoms/FormInputs";
import FileDrop from "@common/components/Molecules/FileDrop";
import { FormField } from "@common/models";
import { SettingsService } from "@pro/_services";
import {
    Notifications,
    PaymentHistory,
    Payments,
} from "@pro/components/Organisms/Settings";
import { PageInternal } from "@pro/containers/PageInternal";
import {cloneDeep} from "lodash";
import React from "react";
import { RouteComponentProps } from "react-router";
import { NavLink } from "react-router-dom";

interface Lookup {
    label: string;
    value: string;
}

interface SettingsProps extends RouteComponentProps<any> {}

interface SettingsState {
    activePane: string;
    contactSubmitButtonMessage: string;
    passwordSubmitButtonMessage: string;
    triggerProfileTickle: boolean;
    showError: boolean;
    errorMessage: string;
    agentSubmitButtonMessage: string;
    updating: boolean;
    updatingMessage: string;
    contactFormIsValid: boolean;
    contactFields: {
        first_name: FormField;
        last_name: FormField;
        address1: FormField;
        primary_location_id: FormField;
        email: FormField;
        profile_image_file_id: FormField;
        phone_number: FormField;
    };
    agentFormIsValid: boolean;
    agentFields: {
        secondary_locations: FormField;
        secondary_job_positions: FormField;
    };
    profileImageUrl: string;
}

export class Settings extends React.Component<SettingsProps, SettingsState> {
    private _abortControllers: { [key: string]: AbortController } = {};

    panes: string[];
    firstName: any;
    fileDropper: any;

    constructor(props) {
        super(props);

        this.panes = [
            Routing.Client.Pro.Profile.Panes.CONTACT_INFO,
            Routing.Client.Pro.Profile.Panes.PASSWORD,
            Routing.Client.Pro.Profile.Panes.NOTIFICATIONS,
            Routing.Client.Pro.Profile.Panes.SOCIAL_NETWORKS,
            Routing.Client.Pro.Profile.Panes.AGENT_SETTINGS,
            Routing.Client.Pro.Profile.Panes.PAYMENT_INFORMATION,
            Routing.Client.Pro.Profile.Panes.PAYMENT_HISTORY,
        ];

        this.state = {
            activePane: Routing.Client.Pro.Profile.Panes.CONTACT_INFO,
            contactSubmitButtonMessage: "Save",
            passwordSubmitButtonMessage: "Save",
            triggerProfileTickle: false,
            showError: false,
            profileImageUrl: "",
            errorMessage: "",
            agentSubmitButtonMessage: "Save",
            updating: false,
            updatingMessage: "loading",
            contactFormIsValid: true,
            contactFields: {
                first_name: {
                    name: "first_name",
                    value: "",
                    label: "",
                    isValid: true,
                    validationMessages: [],
                    rules: [Rules.required],
                },
                last_name: {
                    name: "last_name",
                    value: "",
                    label: "",
                    isValid: true,
                    validationMessages: [],
                    rules: [Rules.required],
                },
                address1: {
                    name: "address1",
                    value: "",
                    label: "",
                    isValid: true,
                    validationMessages: [],
                    rules: [Rules.required],
                },
                primary_location_id: {
                    name: "primary_location_id",
                    value: "",
                    label: "",
                    isValid: true,
                    validationMessages: [],
                    rules: [Rules.required],
                },
                email: {
                    name: "email",
                    value: "",
                    label: "",
                    isValid: true,
                    validationMessages: [],
                    rules: [Rules.required, Rules.formatEmail],
                },
                profile_image_file_id: {
                    name: "profile_image_file_id",
                    value: null,
                    label: "",
                    isValid: true,
                    validationMessages: [],
                    rules: [],
                },
                phone_number: {
                    name: "phone_number",
                    value: "",
                    label: "",
                    isValid: true,
                    validationMessages: [],
                    rules: [Rules.required, Rules.formatPhone],
                },
            },
            agentFormIsValid: true,
            agentFields: {
                secondary_locations: {
                    name: "secondary_locations",
                    value: [],
                    label: "",
                    isValid: true,
                    validationMessages: [],
                    rules: [Rules.required],
                },
                secondary_job_positions: {
                    name: "secondary_job_positions",
                    value: [],
                    label: "",
                    isValid: true,
                    validationMessages: [],
                    rules: [Rules.required],
                },
            },
        };

        this.setActivePane = this.setActivePane.bind(this);
        this.loadDataForActivePane = this.loadDataForActivePane.bind(this);
        this.submitContactInfo = this.submitContactInfo.bind(this);
        this.onContactInfoChange = this.onContactInfoChange.bind(this);

        this.submitAgentSettings = this.submitAgentSettings.bind(this);
        this.onAgentsChange = this.onAgentsChange.bind(this);

        this.handleAddProfileImageFile = this.handleAddProfileImageFile.bind(
            this
        );
        this.handleRemoveProfileImageFile = this.handleRemoveProfileImageFile.bind(
            this
        );
        this.handleProfileImageFileError = this.handleProfileImageFileError.bind(
            this
        );
        this.handleProfileImageFileUploading = this.handleProfileImageFileUploading.bind(
            this
        );
        this.handleProfileImageFileUploaded = this.handleProfileImageFileUploaded.bind(
            this
        );
        this.getPrimaryLocations = this.getPrimaryLocations.bind(this);
        this.getSecondaryLocations = this.getSecondaryLocations.bind(this);
        this.suggestPositions = this.suggestPositions.bind(this);
        this.setUpdating = this.setUpdating.bind(this);
    }

    componentDidMount() {
        const activePane =
            this.props.match.params.pane &&
            this.panes.includes(this.props.match.params.pane)
                ? this.props.match.params.pane
                : Routing.Client.Pro.Profile.Panes.CONTACT_INFO;
        this.setActivePane(activePane);
        this.loadDataForActivePane(activePane);
    }

    componentWillReceiveProps(nextProps) {
        if (this.state.activePane !== nextProps.match.params.pane) {
            this.setActivePane(nextProps.match.params.pane);
            this.setState({ showError: false, errorMessage: "" });
            this.loadDataForActivePane(nextProps.match.params.pane);
        }
    }

    componentWillUnmount() {
        const abortControllers = this._abortControllers;
        if (Object.keys(abortControllers).length > 0) {
            Object.keys(abortControllers).map(key =>
                abortControllers[key].abort()
            );
        }
    }

    setUpdating(value: boolean) {
        this.setState({ updating: value });
    }
    setActivePane(pane) {
        this.setState({
            activePane: pane,
        });
    }

    loadDataForActivePane(pane: string) {
        if (pane === Routing.Client.Pro.Profile.Panes.CONTACT_INFO) {
            this.setState(
                {
                    updating: true,
                    updatingMessage: "loading",
                },
                () => {
                    // Check to see if there already was an abortController, and if so, call abort on it
                    if (this._abortControllers.getContactSettings) {
                        this._abortControllers.getContactSettings.abort();
                    }
                    this._abortControllers.getContactSettings = new AbortController();
                    const signal = this._abortControllers.getContactSettings
                        .signal;

                    SettingsService.getContactSettings(signal)
                        .then(settings => {
                            this.setState(prevState => {
                                const fields = prevState.contactFields;
                                FormHandler.setFieldValuesFromObject(
                                    fields,
                                    settings,
                                    false
                                );
                                fields.primary_location_id.value = {
                                    label: settings.primary_location_name,
                                    value: settings.primary_location_id,
                                };
                                return {
                                    contactFields: fields,
                                    profileImageUrl: settings.profile_image_url,
                                    updating: false,
                                };
                            });
                        })
                        .catch(err => {
                            if (
                                err.type === "abort" ||
                                err.name === "AbortError"
                            ) {
                                this.setState({ updating: false });
                                return;
                            }

                            this.setState({
                                showError: true,
                                errorMessage: err.message,
                                updating: false,
                            });
                        });
                }
            );
        } else if (pane === Routing.Client.Pro.Profile.Panes.AGENT_SETTINGS) {
            this.setState(
                {
                    updating: true,
                    updatingMessage: "loading",
                },
                () => {
                    this._abortControllers.getAgentSettings = new AbortController();
                    const getAgentSettingsSignal = this._abortControllers
                        .getAgentSettings.signal;

                    const agentPromise = SettingsService.getAgentSettings(
                        getAgentSettingsSignal
                    ).then(settings => {
                        this.setState(prevState => {
                            const fields = prevState.agentFields;
                            FormHandler.setFieldValuesFromObject(
                                fields,
                                settings,
                                false
                            );
                            return { agentFields: fields };
                        });

                        Promise.all([agentPromise])
                            .then(x => this.setState({ updating: false }))
                            .catch(err => {
                                if (
                                    err.type === "abort" ||
                                    err.name === "AbortError"
                                ) {
                                    this.setState({ updating: false });
                                    return;
                                }

                                this.setState({
                                    showError: true,
                                    errorMessage: err.message,
                                    updating: false,
                                });
                            });
                    });
                }
            );
        }
    }

    handleAddProfileImageFile(fileId: number) {
        const fields = cloneDeep(this.state.contactFields) as any;
        fields.profile_image_file_id.value = fileId;
        fields.profile_image_file_id.isValid = true;
        this.setState({ contactFields: fields });

    }

    handleRemoveProfileImageFile(fileId: number) {
        const fields = cloneDeep(this.state.contactFields) as any;
        fields.profile_image_file_id.value = null;
        fields.profile_image_file_id.isValid = true;
        this.setState({ contactFields: fields });
    }

    handleProfileImageFileUploading() {
        this.setState({ updating: true });
    }

    handleProfileImageFileUploaded() {
        this.setState({ updating: false });
    }

    handleProfileImageFileError(errMsg: string) {
        this.setState({ updating: false });
        const fields = cloneDeep(this.state.contactFields) as any;
        fields.profile_image_file_id.validationMessages.length = 0;
        fields.profile_image_file_id.validationMessages.push(errMsg);
        fields.profile_image_file_id.isValid = false;
        this.setState({ contactFields: fields });
    }

    getPrimaryLocations(term: string): Promise<Lookup[]> {
        // Check to see if there already was an abortController, and if so, call abort on it
        if (!!this._abortControllers.getPrimaryLocations) {
            this._abortControllers.getPrimaryLocations.abort();
        }
        this._abortControllers.getPrimaryLocations = new AbortController();
        const signal = this._abortControllers.getPrimaryLocations.signal;

        return LocationService.suggestLocations(
            term,
            false,
            false,
            signal
        ).then(result => {
            return result.results.map(x => ({
                label: x.item_name,
                value: x.item_id,
            }));
        });
    }

    getSecondaryLocations(term: string): Promise<Lookup[]> {
        // Check to see if there already was an abortController, and if so, call abort on it
        if (!!this._abortControllers.getSecondaryLocations) {
            this._abortControllers.getSecondaryLocations.abort();
        }
        this._abortControllers.getSecondaryLocations = new AbortController();
        const signal = this._abortControllers.getSecondaryLocations.signal;

        return LocationService.suggestLocations(term, true, true, signal).then(
            result => {
                return result.results.map(x => ({
                    label: x.item_name,
                    value: x.item_id,
                }));
            }
        );
    }

    suggestPositions(query: string): Promise<Lookup[]> {
        // Check to see if there already was an abortController, and if so, call abort on it
        if (this._abortControllers.lookupJobPositions) {
            this._abortControllers.lookupJobPositions.abort();
        }
        this._abortControllers.lookupJobPositions = new AbortController();
        const signal = this._abortControllers.lookupJobPositions.signal;

        return SettingsService.lookupJobPositions(
            query,
            true,
            false,
            signal
        ).then(result => {
            return result.results.map(x => ({
                label: x.item_name,
                value: x.item_id,
            }));
        });
    }

    onContactInfoChange(name: string, value: string) {
        const fields = this.state.contactFields;
        FormHandler.handleInputChange(name, value, fields);
        this.setState({ contactFields: fields });
    }

    submitContactInfo(event: any) {
        event.preventDefault();

        const fields = this.state.contactFields;
        const result = FormHandler.validate(fields);
        const values = FormHandler.getAllValuesAsObject(fields);

        if (result) {
            this.setState(
                {
                    updating: true,
                    updatingMessage: "saving",
                    showError: false,
                    errorMessage: "",
                    contactFields: fields,
                    contactSubmitButtonMessage: "Saving...",
                    contactFormIsValid: true,
                },
                () => {
                    values.primary_location_id = this.state.contactFields.primary_location_id.value.value; //fix select value
                    UserService.saveContactSettings(values)
                        .then(resp => {
                            this.setState({
                                updating: false,
                                contactSubmitButtonMessage: "Saved",
                            });
                            this.fileDropper.reset();

                            UserService.updateProfileImage(resp.result);

                            //trigger parent Method on Page internal
                            this.setState({
                                triggerProfileTickle: !this.state
                                    .triggerProfileTickle,
                                profileImageUrl: resp.result,
                            });
                        })
                        .catch(err =>
                            this.setState({
                                updating: false,
                                showError: true,
                                errorMessage:
                                    err.message + " Please try again.",
                                contactSubmitButtonMessage: "Save"
                            })
                        );
                }
            );
        } else {
            this.setState({
                updating: false,
                contactSubmitButtonMessage: "Save",
                contactFormIsValid: false,
            });
        }
    }

    onAgentsChange(name: string, value: string) {
        const fields = this.state.agentFields;
        FormHandler.handleInputChange(name, value, fields);
        this.setState({ agentFields: fields });
    }

    submitAgentSettings(event: any) {
        event.preventDefault();

        const fields = this.state.agentFields;
        const result = FormHandler.validate(fields);

        this.setState(
            {
                agentFields: fields,
                showError: false,
                errorMessage: "",
                updating: true,
                updatingMessage: "saving",
                agentFormIsValid: true,
            },
            () => {
                if (result) {
                    this.setState(
                        { agentSubmitButtonMessage: "Saving..." },
                        () => {
                            const agentRequest = FormHandler.getAllValuesAsObject(
                                fields
                            );

                            SettingsService.saveAgentSettings(agentRequest)
                                .then(() =>
                                    this.setState({
                                        updating: false,
                                        agentSubmitButtonMessage: "Save",
                                        agentFields: fields,
                                    })
                                )
                                .catch(err =>
                                    this.setState({
                                        updating: false,
                                        showError: true,
                                        errorMessage:
                                            err.message + " Please try again.",
                                        agentSubmitButtonMessage: "Save"
                                    })
                                );
                        }
                    );
                } else {
                    this.setState({
                        updating: false,
                        agentSubmitButtonMessage: "Save",
                        agentFormIsValid: false,
                    });
                }
            }
        );
    }

    render() {
        const { activePane } = this.state;

        return (
            <PageInternal
                size="md"
                title="settings"
                updating={this.state.updating}
                triggerProfileTickle={this.state.triggerProfileTickle}
                updatingMessage={this.state.updatingMessage}
            >
                <section className="c-settings">
                    <section className="c-settings__nav">
                        <ul>
                            <li>
                                <NavLink
                                    to={formatClientRoute(
                                        Routing.Client.Pro.Profile
                                            .SETTINGS_PANE,
                                        {
                                            pane:
                                                Routing.Client.Pro.Profile.Panes
                                                    .CONTACT_INFO,
                                        }
                                    )}
                                    className="c-link no-underline"
                                    activeClassName="is-active"
                                    onClick={() =>
                                        this.setActivePane(
                                            Routing.Client.Pro.Profile.Panes
                                                .CONTACT_INFO
                                        )
                                    }
                                >
                                    contact info
                                </NavLink>
                            </li>
                            <li>
                                <NavLink
                                    to={formatClientRoute(
                                        Routing.Client.Pro.Profile
                                            .SETTINGS_PANE,
                                        {
                                            pane:
                                                Routing.Client.Pro.Profile.Panes
                                                    .NOTIFICATIONS,
                                        }
                                    )}
                                    className="c-link no-underline"
                                    activeClassName="is-active"
                                    onClick={() =>
                                        this.setActivePane(
                                            Routing.Client.Pro.Profile.Panes
                                                .NOTIFICATIONS
                                        )
                                    }
                                >
                                    notifications
                                </NavLink>
                            </li>
                            <li>
                                <NavLink
                                    to={formatClientRoute(
                                        Routing.Client.Pro.Profile
                                            .SETTINGS_PANE,
                                        {
                                            pane:
                                                Routing.Client.Pro.Profile.Panes
                                                    .AGENT_SETTINGS,
                                        }
                                    )}
                                    className="c-link no-underline"
                                    activeClassName="is-active"
                                    onClick={() =>
                                        this.setActivePane(
                                            Routing.Client.Pro.Profile.Panes
                                                .AGENT_SETTINGS
                                        )
                                    }
                                >
                                    agent settings
                                </NavLink>
                            </li>
                            <li>
                                <NavLink
                                    to={formatClientRoute(
                                        Routing.Client.Pro.Profile
                                            .SETTINGS_PANE,
                                        {
                                            pane:
                                                Routing.Client.Pro.Profile.Panes
                                                    .PAYMENT_INFORMATION,
                                        }
                                    )}
                                    className="c-link no-underline"
                                    activeClassName="is-active"
                                    onClick={() =>
                                        this.setActivePane(
                                            Routing.Client.Pro.Profile.Panes
                                                .PAYMENT_INFORMATION
                                        )
                                    }
                                >
                                    payment information
                                </NavLink>
                            </li>
                            <li>
                                <NavLink
                                    to={formatClientRoute(
                                        Routing.Client.Pro.Profile
                                            .SETTINGS_PANE,
                                        {
                                            pane:
                                                Routing.Client.Pro.Profile.Panes
                                                    .PAYMENT_HISTORY,
                                        }
                                    )}
                                    className="c-link no-underline"
                                    activeClassName="is-active"
                                    onClick={() =>
                                        this.setActivePane(
                                            Routing.Client.Pro.Profile.Panes
                                                .PAYMENT_HISTORY
                                        )
                                    }
                                >
                                    payment history
                                </NavLink>
                            </li>
                        </ul>
                    </section>

                    <section className="c-settings__panes">
                        {activePane ===
                            Routing.Client.Pro.Profile.Panes.CONTACT_INFO && (
                            <section className="c-settings__pane">
                                <h2 className="c-settings__title">
                                    contact info
                                </h2>

                                <form>
                                    <Input
                                        name="first_name"
                                        label="first name"
                                        value={
                                            this.state.contactFields.first_name
                                                .value
                                        }
                                        type="text"
                                        isValid={
                                            this.state.contactFields.first_name
                                                .isValid
                                        }
                                        validationMessages={
                                            this.state.contactFields.first_name
                                                .validationMessages
                                        }
                                        onChange={this.onContactInfoChange}
                                        onEnterKey={this.submitContactInfo}
                                        maxLength={
                                            UserConstants.Validations.FirstName
                                                .MAX_LENGTH
                                        }
                                        inputRef={el => (this.firstName = el)}
                                    />

                                    <Input
                                        name="last_name"
                                        label="last name"
                                        value={
                                            this.state.contactFields.last_name
                                                .value
                                        }
                                        type="text"
                                        isValid={
                                            this.state.contactFields.last_name
                                                .isValid
                                        }
                                        validationMessages={
                                            this.state.contactFields.last_name
                                                .validationMessages
                                        }
                                        onChange={this.onContactInfoChange}
                                        onEnterKey={this.submitContactInfo}
                                        maxLength={
                                            UserConstants.Validations.LastName
                                                .MAX_LENGTH
                                        }
                                    />

                                    <Input
                                        name="address1"
                                        label="street address"
                                        value={
                                            this.state.contactFields.address1
                                                .value
                                        }
                                        type="text"
                                        isValid={
                                            this.state.contactFields.address1
                                                .isValid
                                        }
                                        validationMessages={
                                            this.state.contactFields.address1
                                                .validationMessages
                                        }
                                        onChange={this.onContactInfoChange}
                                        onEnterKey={this.submitContactInfo}
                                        maxLength={200}
                                    />

                                    <Select
                                        name="primary_location_id"
                                        label="location"
                                        value={
                                            this.state.contactFields
                                                .primary_location_id.value
                                        }
                                        isValid={
                                            this.state.contactFields
                                                .primary_location_id.isValid
                                        }
                                        validationMessages={
                                            this.state.contactFields
                                                .primary_location_id
                                                .validationMessages
                                        }
                                        onChange={this.onContactInfoChange}
                                        multi={false}
                                        remoteOptionsMethod={
                                            this.getPrimaryLocations
                                        }
                                    />

                                    <Input
                                        name="email"
                                        label="email address"
                                        value={
                                            this.state.contactFields.email.value
                                        }
                                        type="text"
                                        isValid={
                                            this.state.contactFields.email
                                                .isValid
                                        }
                                        validationMessages={
                                            this.state.contactFields.email
                                                .validationMessages
                                        }
                                        onChange={this.onContactInfoChange}
                                        onEnterKey={this.submitContactInfo}
                                    />

                                    <div
                                        className="c-block"
                                        style={{ margin: "40px 0 24px" }}
                                    >
                                        <div className="c-block-title u-text-small">
                                            profile picture
                                        </div>
                                        <div style={{ margin: "24px 0" }}>
                                            {this.state.profileImageUrl && (
                                                <div className="profileimagefloater">
                                                    <img
                                                        src={
                                                            this.state
                                                                .profileImageUrl
                                                        }
                                                        alt="profile"
                                                        width="100"
                                                        height="100"
                                                    />
                                                </div>
                                            )}
                                            <div className="profileimagefloater drop">
                                                <FileDrop
                                                    fileCategory={
                                                        Defaults
                                                            .FileCategoryType
                                                            .UserProfile
                                                    }
                                                    name="profile_image_file_id"
                                                    isMultiple={false}
                                                    isValid={this.state.contactFields.profile_image_file_id.isValid}
                                                    handleAdd={
                                                        this
                                                            .handleAddProfileImageFile
                                                    }
                                                    handleRemove={
                                                        this
                                                            .handleRemoveProfileImageFile
                                                    }
                                                    onFileUploading={
                                                        this
                                                            .handleProfileImageFileUploading
                                                    }
                                                    onFileUploaded={
                                                        this
                                                            .handleProfileImageFileUploaded
                                                    }
                                                    onFileError={
                                                        this
                                                            .handleProfileImageFileError
                                                    }
                                                    validationMessages={
                                                        this.state.contactFields
                                                            .profile_image_file_id
                                                            .validationMessages
                                                    }
                                                    ref={input => {
                                                        this.fileDropper = input;
                                                    }}
                                                />
                                            </div>
                                            <div className="profileimagefixer" />
                                        </div>
                                    </div>

                                    <Input
                                        name="phone_number"
                                        label="mobile phone"
                                        value={
                                            this.state.contactFields
                                                .phone_number.value
                                        }
                                        type="tel"
                                        isValid={
                                            this.state.contactFields
                                                .phone_number.isValid
                                        }
                                        validationMessages={
                                            this.state.contactFields
                                                .phone_number.validationMessages
                                        }
                                        onChange={this.onContactInfoChange}
                                        onEnterKey={this.submitContactInfo}
                                        maxLength={
                                            UserConstants.Validations
                                                .PhoneNumber.MAX_LENGTH
                                        }
                                    />

                                    <div className="c-block">
                                        <ContactError
                                            show={
                                                !this.state
                                                    .contactFormIsValid ||
                                                this.state.showError
                                            }
                                            title={
                                                this.state.showError
                                                    ? "error saving contact info"
                                                    : ""
                                            }
                                            description={
                                                this.state.errorMessage
                                            }
                                        />
                                        <Button
                                            id="contact-submit-button-message"
                                            large={true}
                                            green={true}
                                            onClick={this.submitContactInfo}
                                        >
                                            {
                                                this.state
                                                    .contactSubmitButtonMessage
                                            }
                                        </Button>
                                    </div>
                                </form>
                            </section>
                        )}

                        {activePane ===
                            Routing.Client.Pro.Profile.Panes.NOTIFICATIONS && (
                            <Notifications updating={this.setUpdating} />
                        )}

                        {activePane ===
                            Routing.Client.Pro.Profile.Panes.AGENT_SETTINGS && (
                            <section className="c-settings__pane">
                                <h2 className="c-settings__title">
                                    agent settings
                                </h2>

                                <Select
                                    name="secondary_job_positions"
                                    label="job categories"
                                    description="Select positions based on yours or your network's work experience"
                                    value={
                                        this.state.agentFields
                                            .secondary_job_positions.value
                                    }
                                    isValid={
                                        this.state.agentFields
                                            .secondary_job_positions.isValid
                                    }
                                    validationMessages={
                                        this.state.agentFields
                                            .secondary_job_positions
                                            .validationMessages
                                    }
                                    onChange={this.onAgentsChange}
                                    multi={true}
                                    remoteOptionsMethod={this.suggestPositions}
                                />

                                <Select
                                    name="secondary_locations"
                                    label="secondary locations"
                                    description="Select locations where you have lived or worked"
                                    value={
                                        this.state.agentFields
                                            .secondary_locations.value
                                    }
                                    isValid={
                                        this.state.agentFields
                                            .secondary_locations.isValid
                                    }
                                    validationMessages={
                                        this.state.agentFields
                                            .secondary_locations
                                            .validationMessages
                                    }
                                    onChange={this.onAgentsChange}
                                    multi={true}
                                    remoteOptionsMethod={
                                        this.getSecondaryLocations
                                    }
                                />

                                <div className="c-block">
                                    <ContactError
                                        show={
                                            !this.state.agentFormIsValid ||
                                            this.state.showError
                                        }
                                        title={
                                            this.state.showError
                                                ? "error saving agent settings"
                                                : ""
                                        }
                                        description={this.state.errorMessage}
                                    />
                                    <Button
                                        id="settings-agent-submit-button"
                                        large={true}
                                        green={true}
                                        onClick={this.submitAgentSettings}
                                    >
                                        {this.state.agentSubmitButtonMessage}
                                    </Button>
                                </div>
                            </section>
                        )}

                        {activePane ===
                            Routing.Client.Pro.Profile.Panes
                                .PAYMENT_INFORMATION && <Payments />}

                        {activePane ===
                            Routing.Client.Pro.Profile.Panes
                                .PAYMENT_HISTORY && <PaymentHistory />}
                    </section>
                </section>
            </PageInternal>
        );
    }
}
